import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Users, UserPlus, Edit2, Trash2, Shield, Key, Search,
  Download, ChevronDown, ChevronUp,
  Lock, Mail, MoreVertical, AlertCircle,
  Eye, UserCheck, UserX, Plus, Activity, RefreshCw,
  CheckCircle, XCircle, Clock, Database, Bot, Zap
} from 'lucide-react';
import { GlobalNavigation } from '../../common/GlobalNavigation';
import { useAuth } from '../../../contexts/AuthContext';
import { userService, User, Role, Permission } from '../../../services/api/userService';

// Using User interface from userService

// Using Role interface from userService

// Using Permission interface from userService

// API Status interface
interface ApiStatus {
  name: string;
  status: 'connected' | 'disconnected' | 'error' | 'not_configured';
  message: string;
  lastChecked: string;
  responseTime?: number;
  details?: Record<string, any>;
}

interface ApiStatusResponse {
  success: boolean;
  data: ApiStatus[];
  summary: {
    total: number;
    connected: number;
    disconnected: number;
    notConfigured: number;
  };
}

export const AdminDashboard: React.FC = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  // Row action menu. Rendered in a portal with fixed positioning because the
  // table scroll container clips absolutely positioned children.
  const [openMenu, setOpenMenu] = useState<
    { userId: string; top: number; left: number; triggerTop: number; triggerBottom: number } | null
  >(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'permissions' | 'api-status'>('users');
  const [apiStatuses, setApiStatuses] = useState<ApiStatus[]>([]);
  const [apiStatusLoading, setApiStatusLoading] = useState(false);
  const [apiStatusSummary, setApiStatusSummary] = useState<ApiStatusResponse['summary'] | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [sortField, setSortField] = useState<keyof User>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // Form states
  const [userForm, setUserForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    role: '',
    department: '',
    phone: '',
    password: '',
    loginMethod: 'password' as 'password' | 'microsoft',
    sendWelcomeEmail: true
  });

  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });

  // Load data from API
  const loadData = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Load users, roles, and permissions in parallel
      const [usersResponse, rolesResponse, permissionsResponse] = await Promise.all([
        userService.getUsers(token, { page: 1, limit: 100 }),
        userService.getRoles(token),
        userService.getPermissions(token)
      ]);
      
      // Handle users response structure: { users: [...], pagination: {...} }
      setUsers(usersResponse.users.map(user => ({
        ...user,
        id: user._id || user.id
      })));
      
      // Handle roles response structure: direct array
      setRoles(rolesResponse.map(role => ({
        ...role,
        id: role._id || role.id
      })));
      
      // Handle permissions response structure: direct array
      setPermissions(permissionsResponse.map(permission => ({
        ...permission,
        id: permission._id || permission.id
      })));
      
    } catch (error) {
      console.error('Failed to load data:', error);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load API status
  const loadApiStatus = useCallback(async () => {
    if (!token) return;

    try {
      setApiStatusLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL}/api/admin/api-status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch API status');
      }

      const data: ApiStatusResponse = await response.json();

      if (data.success) {
        setApiStatuses(data.data);
        setApiStatusSummary(data.summary);
      }
    } catch (error) {
      console.error('Failed to load API status:', error);
      setError('Failed to load API status. Please try again.');
    } finally {
      setApiStatusLoading(false);
    }
  }, [token]);

  // Load API status when tab is selected
  useEffect(() => {
    if (activeTab === 'api-status') {
      loadApiStatus();
    }
  }, [activeTab, loadApiStatus]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    
    try {
      const { password, sendWelcomeEmail, ...profile } = userForm;

      // Microsoft-only accounts are created without a password, so there is no
      // second credential that could be used against the email/password form.
      await userService.createUser(token, {
        ...profile,
        sendWelcomeEmail,
        ...(userForm.loginMethod === 'password' ? { password } : {})
      } as any);
      
      // Reload data to get the complete user information
      await loadData();
      setShowUserModal(false);
      resetUserForm();
    } catch (error) {
      console.error('Failed to create user:', error);
      setError('Failed to create user. Please try again.');
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser || !token) return;
    
    try {
      const { password, sendWelcomeEmail, ...profile } = userForm;

      // Never send an empty password on update - it would fail validation.
      await userService.updateUser(token, editingUser.id, {
        ...profile,
        ...(password ? { password } : {})
      } as any);
      
      // Reload data to get the complete updated user information
      await loadData();
      setShowUserModal(false);
      setEditingUser(null);
      resetUserForm();
    } catch (error) {
      console.error('Failed to update user:', error);
      setError('Failed to update user. Please try again.');
    }
  };

  const handleToggleUserStatus = async (userId: string, user: User) => {
    // Check if user is active based on both status and isActive fields
    const isUserActive = (user.status === 'active' || user.status === undefined) && (user.isActive !== false);
    const action = isUserActive ? 'deactivate' : 'reactivate';
    const confirmMessage = isUserActive 
      ? 'Are you sure you want to deactivate this user? They will lose access to the system but their data will be preserved.'
      : 'Are you sure you want to reactivate this user? They will regain access to the system.';
    
    if (!token || !window.confirm(confirmMessage)) return;
    
    try {
      if (isUserActive) {
        // Deactivate user (soft delete)
        await userService.deleteUser(token, userId);
      } else {
        // Reactivate user
        await userService.updateUser(token, userId, { status: 'active', isActive: true });
      }
      
      // Reload data to get the updated user information
      await loadData();
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      setError(`Failed to ${action} user. Please try again.`);
    }
  };

  // Role creation handler
  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    
    try {
      await userService.createRole(token, {
        name: roleForm.name,
        description: roleForm.description,
        permissions: roleForm.permissions
      });
      
      // Reload data to get the complete role information
      await loadData();
      setShowRoleModal(false);
      setRoleForm({ name: '', description: '', permissions: [] });
    } catch (error) {
      console.error('Failed to create role:', error);
      setError('Failed to create role. Please try again.');
    }
  };

  const handleUpdateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRole || !token) return;
    
    // Prevent updating Admin role
    if (editingRole.name === 'Admin') {
      setError('Admin role cannot be modified for security reasons.');
      return;
    }
    
    try {
      await userService.updateRole(token, editingRole.id, {
        name: roleForm.name,
        description: roleForm.description,
        permissions: roleForm.permissions
      });
      
      // Reload data to get the complete updated role information
      await loadData();
      setShowRoleModal(false);
      setEditingRole(null);
      setRoleForm({ name: '', description: '', permissions: [] });
    } catch (error) {
      console.error('Failed to update role:', error);
      setError('Failed to update role. Please try again.');
    }
  };

  const handlePermissionMatrixChange = async (role: Role, permissionCode: string, isChecked: boolean) => {
    if (!token) return;
    
    // Prevent modifying system roles
    if (role.isSystem) {
      setError(`${role.name} is a system role and cannot be modified for security reasons.`);
      return;
    }
    
    try {
      let updatedPermissions: string[];
      
      if (isChecked) {
        // Add permission if not already present
        updatedPermissions = role.permissions.includes(permissionCode) 
          ? role.permissions 
          : [...role.permissions, permissionCode];
      } else {
        // Remove permission
        updatedPermissions = role.permissions.filter(p => p !== permissionCode);
      }
      
      // Update role permissions via API
      await userService.updateRole(token, role.id, {
        name: role.name,
        description: role.description,
        permissions: updatedPermissions
      });
      
      // Update local state to reflect the change immediately
      setRoles(roles.map(r => 
        r.id === role.id 
          ? { ...r, permissions: updatedPermissions }
          : r
      ));
      
    } catch (error) {
      console.error('Failed to update role permissions:', error);
      setError('Failed to update role permissions. Please try again.');
      
      // Reload data to ensure consistency
      await loadData();
    }
  };

  const toggleModuleExpansion = (module: string) => {
    const newExpandedModules = new Set(expandedModules);
    if (newExpandedModules.has(module)) {
      newExpandedModules.delete(module);
    } else {
      newExpandedModules.add(module);
    }
    setExpandedModules(newExpandedModules);
  };

  const handleBulkAction = async (action: string) => {
    switch (action) {
      case 'activate':
        setUsers(users.map(u => selectedUsers.has(u.id) ? { ...u, status: 'active' } : u));
        break;
      case 'deactivate':
        setUsers(users.map(u => selectedUsers.has(u.id) ? { ...u, status: 'inactive' } : u));
        break;
      case 'delete':
        if (window.confirm(`Are you sure you want to delete ${selectedUsers.size} users?`)) {
          setUsers(users.filter(u => !selectedUsers.has(u.id)));
        }
        break;
    }
    setSelectedUsers(new Set());
    setShowBulkActions(false);
  };

  const resetUserForm = () => {
    setUserForm({
      email: '',
      firstName: '',
      lastName: '',
      role: '',
      department: '',
      phone: '',
      password: '',
      loginMethod: 'password',
      sendWelcomeEmail: true
    });
  };

  /**
   * Open the row action menu anchored to its trigger button.
   *
   * The menu is positioned in viewport coordinates and portalled to <body>, so
   * the table's overflow container cannot clip it. Flips above the button when
   * there is not enough room below.
   */
  const toggleRowMenu = (userId: string, trigger: HTMLElement) => {
    if (openMenu?.userId === userId) {
      setOpenMenu(null);
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const MENU_WIDTH = 192; // w-48
    const GAP = 8;

    setOpenMenu({
      userId,
      // Provisional placement below the trigger; the layout effect below
      // corrects it once the menu's real height is known.
      top: rect.bottom + GAP,
      // Right-align to the trigger, clamped so it never runs off-screen.
      left: Math.max(GAP, Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - GAP)),
      triggerTop: rect.top,
      triggerBottom: rect.bottom
    });
  };

  // Correct the menu's vertical placement against its measured height, before
  // paint. Guessing the height in the click handler is fragile - it changes
  // whenever a menu item is added or the font metrics differ.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!openMenu || !el) return;

    const GAP = 8;
    const height = el.offsetHeight;
    const fitsBelow = openMenu.triggerBottom + GAP + height <= window.innerHeight;
    const fitsAbove = openMenu.triggerTop - GAP - height >= 0;

    const desiredTop = fitsBelow
      ? openMenu.triggerBottom + GAP
      : fitsAbove
        ? openMenu.triggerTop - GAP - height
        // Fits neither side (very short viewport): clamp inside the viewport.
        : Math.max(GAP, window.innerHeight - height - GAP);

    if (Math.round(desiredTop) !== Math.round(openMenu.top)) {
      setOpenMenu({ ...openMenu, top: desiredTop });
    }
  }, [openMenu]);

  // Dismiss the row menu on outside click, Escape, or any scroll/resize that
  // would leave it visually detached from its trigger.
  useEffect(() => {
    if (!openMenu) return;

    const onPointerDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null);
    };
    const onReflow = () => setOpenMenu(null);

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [openMenu]);

  const handleSort = (field: keyof User) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort users
  const filteredUsers = users
    .filter(user => {
      const matchesSearch = 
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.lastName || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const userIsActive = ((user.status === 'active' || user.status === undefined) && (user.isActive !== false));
      const matchesStatus = filterStatus === 'all' || 
        (filterStatus === 'active' && userIsActive) ||
        (filterStatus === 'inactive' && !userIsActive);
      return matchesSearch && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;
      
      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-100 via-beige-50 to-primary-50 dark:from-dark-bg dark:via-dark-bg-secondary dark:to-dark-bg-tertiary transition-colors duration-300">
      <GlobalNavigation
        title="Admin Dashboard"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Admin Dashboard' }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Elegant Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-accent-400 dark:via-accent-500 dark:to-accent-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <span className="text-lg font-medium text-gray-700 dark:text-white">Loading admin data...</span>
            <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">Please wait while we gather your data</span>
          </div>
        )}

        {/* Elegant Error State */}
        {error && (
          <div className="backdrop-blur-xl bg-error-50/80 dark:bg-error-900/20 border border-error-200/30 dark:border-error-500/20 rounded-2xl p-6 mb-8 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-error-100 dark:bg-error-900/30 rounded-xl">
                <AlertCircle className="h-6 w-6 text-error-500 dark:text-error-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-error-800 dark:text-error-300">Something went wrong</h3>
                <div className="mt-2 text-sm text-error-700 dark:text-error-400 leading-relaxed">{error}</div>
                <div className="mt-4">
                  <button
                    onClick={loadData}
                    className="group px-4 py-2 bg-error-500 text-white rounded-xl hover:bg-error-600 transition-all duration-200 font-medium"
                  >
                    <span className="group-hover:scale-105 transition-transform inline-block">Try Again</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Elegant Header */}
            <div className="backdrop-blur-xl bg-white/70 dark:bg-dark-surface/90 rounded-3xl shadow-2xl dark:shadow-dark-lg border border-white/20 dark:border-dark-border mb-8 overflow-hidden">
              <div className="relative p-8 bg-gradient-to-r from-primary-50/50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/20 backdrop-blur-sm border-b border-white/20 dark:border-dark-border">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-400 via-primary-500 to-primary-600 dark:from-accent-400 dark:via-accent-500 dark:to-accent-600"></div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 dark:from-accent-400 dark:via-accent-500 dark:to-accent-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-light text-gray-800 dark:text-white tracking-tight">Admin Panel</h1>
                      <p className="mt-1 text-gray-600 dark:text-gray-400 font-medium">
                        Manage users, roles, and permissions
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <button className="group px-5 py-3 text-gray-700 dark:text-gray-300 bg-white/40 dark:bg-dark-bg-secondary/60 backdrop-blur-sm border border-white/30 dark:border-dark-border rounded-xl hover:bg-white/60 dark:hover:bg-dark-surface-raised transition-all duration-200 flex items-center gap-2 font-medium">
                      <Download className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                      Export
                    </button>
                    <button
                      onClick={() => {
                        setEditingUser(null);
                        resetUserForm();
                        setShowUserModal(true);
                      }}
                      className="group px-5 py-3 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-xl hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 transition-all duration-200 flex items-center gap-2 font-medium"
                    >
                      <UserPlus className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      Add User
                    </button>
                  </div>
                </div>
              </div>

              {/* Elegant Tabs */}
              <div className="px-8 py-4 bg-white/40 dark:bg-dark-bg-secondary/40 backdrop-blur-sm">
                <nav className="flex space-x-2">
                  {[
                    { id: 'users', label: 'Users', icon: Users, color: 'primary' },
                    { id: 'roles', label: 'Roles', icon: Shield, color: 'purple' },
                    { id: 'permissions', label: 'Permissions', icon: Key, color: 'emerald' },
                    { id: 'api-status', label: 'API Status', icon: Activity, color: 'cyan' }
                  ].map(tab => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id as any)}
                        className={`group px-6 py-4 rounded-2xl font-medium text-sm transition-all duration-200 flex items-center gap-3 ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-r from-primary-500 to-primary-600 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 shadow-lg shadow-primary-500/25 dark:shadow-accent-500/25'
                            : 'bg-white/40 dark:bg-dark-surface/60 text-gray-600 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-dark-surface-raised hover:text-gray-800 dark:hover:text-white'
                        }`}
                      >
                        <div className={`p-2 rounded-xl transition-all duration-200 ${
                          activeTab === tab.id
                            ? 'bg-white/20'
                            : tab.color === 'primary'
                              ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-500 dark:text-primary-400 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/50'
                              : tab.color === 'purple'
                                ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-500 dark:text-purple-400 group-hover:bg-purple-100 dark:group-hover:bg-purple-900/50'
                                : tab.color === 'cyan'
                                  ? 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-500 dark:text-cyan-400 group-hover:bg-cyan-100 dark:group-hover:bg-cyan-900/50'
                                  : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-500 dark:text-emerald-400 group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/50'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div className="text-left">
                          <span className="block">{tab.label}</span>
                          <span className={`text-xs ${activeTab === tab.id ? 'text-white/70 dark:text-gray-700' : 'text-gray-500 dark:text-gray-500'}`}>
                            {tab.id === 'users' ? `${filteredUsers.length} active` :
                             tab.id === 'roles' ? `${roles.length} defined` :
                             tab.id === 'permissions' ? `${permissions.length} total` :
                             apiStatusSummary ? `${apiStatusSummary.connected}/${apiStatusSummary.total} connected` : 'Check status'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-lg dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border">
            {/* Filters */}
            <div className="p-6 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                  />
                </div>
                <div className="flex items-center gap-4">
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    className="pl-4 pr-10 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                  >
                    <option value="all">All Roles</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>{role.name}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="pl-4 pr-10 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                  {selectedUsers.size > 0 && (
                    <div className="relative">
                      <button
                        onClick={() => setShowBulkActions(!showBulkActions)}
                        className="px-4 py-2 bg-gray-100 dark:bg-dark-bg-secondary text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-surface-raised flex items-center gap-2 border border-transparent dark:border-dark-border"
                      >
                        Bulk Actions ({selectedUsers.size})
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      {showBulkActions && (
                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-lg shadow-lg dark:shadow-dark-lg border border-gray-200 dark:border-dark-border z-10">
                          <button
                            onClick={() => handleBulkAction('activate')}
                            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
                          >
                            <UserCheck className="w-4 h-4" />
                            Activate
                          </button>
                          <button
                            onClick={() => handleBulkAction('deactivate')}
                            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
                          >
                            <UserX className="w-4 h-4" />
                            Deactivate
                          </button>
                          <button
                            onClick={() => handleBulkAction('delete')}
                            className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-secondary text-error-600 dark:text-error-400 flex items-center gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
                          } else {
                            setSelectedUsers(new Set());
                          }
                        }}
                        className="h-4 w-4 text-primary-600 dark:text-accent-400 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded bg-white dark:bg-dark-bg-secondary"
                      />
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center gap-1">
                        User
                        {sortField === 'email' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-300"
                      onClick={() => handleSort('lastLogin')}
                    >
                      <div className="flex items-center gap-1">
                        Last Login
                        {sortField === 'lastLogin' && (
                          sortDirection === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-border">
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedUsers);
                            if (e.target.checked) {
                              newSelected.add(user.id);
                            } else {
                              newSelected.delete(user.id);
                            }
                            setSelectedUsers(newSelected);
                          }}
                          className="h-4 w-4 text-primary-600 dark:text-accent-400 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded bg-white dark:bg-dark-bg-secondary"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{user.firstName} {user.lastName}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">@{(user.email || '').split('@')[0] || 'unknown'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        {user.department || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          ((user.status === 'active' || user.status === undefined) && (user.isActive !== false)) ? 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-300' :
                          (user.status === 'inactive' || user.isActive === false) ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300' :
                          'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-300'
                        }`}>
                          {((user.status === 'active' || user.status === undefined) && (user.isActive !== false)) ? 'Active' :
                           (user.status === 'inactive' || user.isActive === false) ? 'Inactive' :
                           user.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingUser(user);
                              setUserForm({
                                email: user.email,
                                firstName: user.firstName,
                                lastName: user.lastName,
                                role: user.role,
                                department: user.department || '',
                                phone: user.phone || '',
                                password: '',
                                loginMethod: user.loginMethod || 'password',
                                sendWelcomeEmail: false
                              });
                              setShowUserModal(true);
                            }}
                            className="text-primary-600 dark:text-accent-400 hover:text-primary-800 dark:hover:text-accent-300"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user)}
                            className={((user.status === 'active' || user.status === undefined) && (user.isActive !== false)) ? "text-error-600 dark:text-error-400 hover:text-error-800 dark:hover:text-error-300" : "text-success-600 dark:text-success-400 hover:text-success-800 dark:hover:text-success-300"}
                            title={((user.status === 'active' || user.status === undefined) && (user.isActive !== false)) ? 'Deactivate User' : 'Reactivate User'}
                          >
                            {((user.status === 'active' || user.status === undefined) && (user.isActive !== false)) ? (
                              <UserX className="w-4 h-4" />
                            ) : (
                              <UserCheck className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={(e) => toggleRowMenu(user.id, e.currentTarget)}
                            aria-haspopup="menu"
                            aria-expanded={openMenu?.userId === user.id}
                            aria-label={`Actions for ${user.firstName} ${user.lastName}`}
                            className={`p-1 rounded transition-colors ${
                              openMenu?.userId === user.id
                                ? 'bg-gray-100 dark:bg-dark-bg-secondary text-gray-700 dark:text-gray-200'
                                : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-secondary'
                            }`}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-400">
                Showing 1 to {filteredUsers.length} of {users.length} users
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border border-gray-300 dark:border-dark-border rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary">Previous</button>
                <button className="px-3 py-1 border border-primary-600 dark:border-accent-400 rounded bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900">1</button>
                <button className="px-3 py-1 border border-gray-300 dark:border-dark-border rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary">Next</button>
              </div>
            </div>
          </div>
        )}

        {/* Roles Tab */}
        {activeTab === 'roles' && (
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-lg dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Manage Roles</h2>
              <button
                onClick={() => {
                  setEditingRole(null);
                  setRoleForm({ name: '', description: '', permissions: [] });
                  setShowRoleModal(true);
                }}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 transition-all duration-200 flex items-center gap-2 font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Role
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {roles.map(role => (
                  <div key={role.id} className="border border-gray-200 dark:border-dark-border rounded-xl p-4 bg-white dark:bg-dark-bg-secondary hover:shadow-md dark:hover:shadow-dark-lg transition-shadow">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white">{role.name}</h3>
                        {role.isSystem && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 mt-1">
                            System
                          </span>
                        )}
                      </div>
                      {!role.isSystem && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              setEditingRole(role);
                              setRoleForm({
                                name: role.name,
                                description: role.description,
                                permissions: role.permissions
                              });
                              setShowRoleModal(true);
                            }}
                            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button className="text-error-400 dark:text-error-500 hover:text-error-600 dark:hover:text-error-400">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{role.description}</p>
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-500 uppercase">Permissions</p>
                      {role.permissions.slice(0, 3).map((perm, index) => (
                        <div key={index} className="text-xs text-gray-600 dark:text-gray-400">• {perm}</div>
                      ))}
                      {role.permissions.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-500">+{role.permissions.length - 3} more</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Permissions Tab */}
        {activeTab === 'permissions' && (
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-lg dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-medium text-gray-900 dark:text-white">Permission Matrix</h2>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300">
                    Click checkboxes to edit permissions
                  </span>
                </div>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Manage role permissions by clicking the checkboxes. System roles (Admin, User, Viewer) are protected and cannot be modified. Only custom roles can be edited.
              </p>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-dark-bg-secondary">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Module</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Permission</th>
                      {roles.map(role => (
                        <th key={role.id} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          <div className="flex flex-col items-start">
                            <span>{role.name}</span>
                            {role.isSystem && (
                              <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                                System
                              </span>
                            )}
                            {!role.isSystem && (
                              <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400">
                                Custom
                              </span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                    {permissions.map(permission => (
                      <tr key={permission.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{permission.module}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{permission.name}</td>
                        {roles.map(role => (
                          <td key={role.id} className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={role.permissions.includes(permission.code)}
                              disabled={role.isSystem}
                              onChange={(e) => handlePermissionMatrixChange(role, permission.code, e.target.checked)}
                              className={`h-4 w-4 text-primary-600 dark:text-accent-400 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded bg-white dark:bg-dark-bg-secondary ${
                                role.isSystem ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* API Status Tab */}
        {activeTab === 'api-status' && (
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-lg dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-medium text-gray-900 dark:text-white">External API Status</h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Monitor the connection status of external services and APIs
                  </p>
                </div>
                <button
                  onClick={loadApiStatus}
                  disabled={apiStatusLoading}
                  className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 dark:from-cyan-500 dark:to-cyan-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-cyan-500/25 dark:hover:shadow-cyan-500/25 transition-all duration-200 flex items-center gap-2 font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${apiStatusLoading ? 'animate-spin' : ''}`} />
                  Refresh Status
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            {apiStatusSummary && (
              <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-dark-bg-secondary/50">
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-dark-surface rounded-xl p-4 border border-gray-200 dark:border-dark-border">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <Activity className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{apiStatusSummary.total}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total APIs</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-dark-surface rounded-xl p-4 border border-success-200 dark:border-success-500/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-success-100 dark:bg-success-900/30 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-success-600 dark:text-success-400">{apiStatusSummary.connected}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Connected</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-dark-surface rounded-xl p-4 border border-error-200 dark:border-error-500/30">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-error-100 dark:bg-error-900/30 rounded-lg">
                        <XCircle className="w-5 h-5 text-error-600 dark:text-error-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-error-600 dark:text-error-400">{apiStatusSummary.disconnected}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Disconnected</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white dark:bg-dark-surface rounded-xl p-4 border border-gray-200 dark:border-dark-border">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <Clock className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{apiStatusSummary.notConfigured}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Not Configured</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {apiStatusLoading && (
              <div className="p-12 flex flex-col items-center justify-center">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg mb-4">
                  <RefreshCw className="w-6 h-6 text-white animate-spin" />
                </div>
                <span className="text-gray-600 dark:text-gray-400">Checking API connections...</span>
              </div>
            )}

            {/* API Status List */}
            {!apiStatusLoading && apiStatuses.length > 0 && (
              <div className="p-6">
                <div className="space-y-4">
                  {apiStatuses.map((api, index) => (
                    <div
                      key={index}
                      className={`p-5 rounded-xl border transition-all duration-200 hover:shadow-md ${
                        api.status === 'connected'
                          ? 'bg-success-50/50 dark:bg-success-900/10 border-success-200 dark:border-success-500/30'
                          : api.status === 'not_configured'
                            ? 'bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700'
                            : 'bg-error-50/50 dark:bg-error-900/10 border-error-200 dark:border-error-500/30'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-xl ${
                            api.status === 'connected'
                              ? 'bg-success-100 dark:bg-success-900/30'
                              : api.status === 'not_configured'
                                ? 'bg-gray-100 dark:bg-gray-800'
                                : 'bg-error-100 dark:bg-error-900/30'
                          }`}>
                            {api.name.toLowerCase().includes('claude') || api.name.toLowerCase().includes('anthropic') ? (
                              <Bot className={`w-6 h-6 ${
                                api.status === 'connected' ? 'text-success-600 dark:text-success-400' :
                                api.status === 'not_configured' ? 'text-gray-500 dark:text-gray-400' :
                                'text-error-600 dark:text-error-400'
                              }`} />
                            ) : api.name.toLowerCase().includes('mongo') || api.name.toLowerCase().includes('database') ? (
                              <Database className={`w-6 h-6 ${
                                api.status === 'connected' ? 'text-success-600 dark:text-success-400' :
                                api.status === 'not_configured' ? 'text-gray-500 dark:text-gray-400' :
                                'text-error-600 dark:text-error-400'
                              }`} />
                            ) : api.name.toLowerCase().includes('openai') ? (
                              <Zap className={`w-6 h-6 ${
                                api.status === 'connected' ? 'text-success-600 dark:text-success-400' :
                                api.status === 'not_configured' ? 'text-gray-500 dark:text-gray-400' :
                                'text-error-600 dark:text-error-400'
                              }`} />
                            ) : (
                              <Activity className={`w-6 h-6 ${
                                api.status === 'connected' ? 'text-success-600 dark:text-success-400' :
                                api.status === 'not_configured' ? 'text-gray-500 dark:text-gray-400' :
                                'text-error-600 dark:text-error-400'
                              }`} />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">{api.name}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{api.message}</p>
                            {api.details && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {Object.entries(api.details).map(([key, value]) => (
                                  <span
                                    key={key}
                                    className="inline-flex items-center px-2 py-1 text-xs rounded-md bg-white/60 dark:bg-dark-bg-secondary/60 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-dark-border"
                                  >
                                    <span className="font-medium mr-1">{key}:</span>
                                    <span>{String(value)}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                            api.status === 'connected'
                              ? 'bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300'
                              : api.status === 'not_configured'
                                ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                                : 'bg-error-100 dark:bg-error-900/30 text-error-700 dark:text-error-300'
                          }`}>
                            {api.status === 'connected' ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : api.status === 'not_configured' ? (
                              <Clock className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                            {api.status === 'connected' ? 'Connected' :
                             api.status === 'not_configured' ? 'Not Configured' :
                             api.status === 'disconnected' ? 'Disconnected' : 'Error'}
                          </span>
                          {api.responseTime && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                              Response: {api.responseTime}ms
                            </p>
                          )}
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            Last checked: {new Date(api.lastChecked).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty State */}
            {!apiStatusLoading && apiStatuses.length === 0 && (
              <div className="p-12 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mb-4">
                  <Activity className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No API Status Data</h3>
                <p className="text-gray-500 dark:text-gray-400 text-center max-w-md mb-4">
                  Click "Refresh Status" to check the connection status of external APIs and services.
                </p>
                <button
                  onClick={loadApiStatus}
                  className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                >
                  Check API Status
                </button>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </div>

      {/* Row action menu - portalled to <body> so the table's overflow container
          cannot clip it, and positioned in viewport coordinates. */}
      {openMenu && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{ position: 'fixed', top: openMenu.top, left: openMenu.left, width: 192 }}
          className="bg-white dark:bg-dark-surface rounded-lg shadow-xl dark:shadow-dark-lg border border-gray-200 dark:border-dark-border py-1 z-[60]"
        >
          <button
            role="menuitem"
            onClick={() => setOpenMenu(null)}
            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
          >
            <Eye className="w-4 h-4" />
            View Details
          </button>
          <button
            role="menuitem"
            onClick={() => setOpenMenu(null)}
            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
          >
            <Key className="w-4 h-4" />
            Reset Password
          </button>
          <button
            role="menuitem"
            onClick={() => setOpenMenu(null)}
            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Send Email
          </button>
          <button
            role="menuitem"
            onClick={() => setOpenMenu(null)}
            className="w-full px-4 py-2 text-left text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-bg-secondary flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Suspend Account
          </button>
        </div>,
        document.body
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-gradient-to-r from-primary-50/50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingUser ? 'Edit User' : 'Add New User'}
              </h2>
            </div>
            <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
                  <input
                    type="text"
                    value={userForm.firstName}
                    onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                  <input
                    type="text"
                    value={userForm.lastName}
                    onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Login Method</label>
                  <select
                    value={userForm.loginMethod}
                    onChange={(e) => setUserForm({ ...userForm, loginMethod: e.target.value as 'password' | 'microsoft' })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                  >
                    <option value="password">Password Login</option>
                    <option value="microsoft">Microsoft Login</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
                    {userForm.loginMethod === 'microsoft'
                      ? 'Signs in with Microsoft only. The email must match their Microsoft account.'
                      : 'Signs in with email and password only.'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400"
                    required
                  >
                    <option value="">Select a role</option>
                    {roles.map(role => (
                      <option key={role.id} value={role.name}>{role.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
                  <input
                    type="text"
                    value={userForm.department}
                    onChange={(e) => setUserForm({ ...userForm, department: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={userForm.phone}
                    onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                    className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                    placeholder="Optional"
                  />
                </div>
                {!editingUser && userForm.loginMethod === 'password' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Temporary Password</label>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                      className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                      required={!editingUser && userForm.loginMethod === 'password'}
                    />
                  </div>
                )}
              </div>
              {!editingUser && (
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={userForm.sendWelcomeEmail}
                      onChange={(e) => setUserForm({ ...userForm, sendWelcomeEmail: e.target.checked })}
                      className="h-4 w-4 text-primary-600 dark:text-accent-400 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded bg-white dark:bg-dark-bg-secondary"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Send welcome email with login instructions</span>
                  </label>
                </div>
              )}


              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  type="button"
                  onClick={() => {
                    setShowUserModal(false);
                    setEditingUser(null);
                    resetUserForm();
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 transition-all duration-200 font-medium"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl dark:shadow-dark-lg border border-gray-200/50 dark:border-dark-border max-w-4xl w-full max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-dark-border bg-gradient-to-r from-primary-50/50 to-primary-100/50 dark:from-primary-900/20 dark:to-primary-800/20">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingRole ? 'Edit Role' : 'Add New Role'}
              </h2>
            </div>
            <form onSubmit={editingRole ? handleUpdateRole : handleCreateRole} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role Name</label>
                <input
                  type="text"
                  value={roleForm.name}
                  onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={roleForm.description}
                  onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 placeholder-gray-500 dark:placeholder-gray-500"
                  required
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Permissions
                    {roleForm.name === 'Admin' && (
                      <span className="ml-2 text-xs text-error-600 dark:text-error-400 font-normal">(Locked - Admin role cannot be modified)</span>
                    )}
                  </label>
                  {roleForm.name !== 'Admin' && (
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setRoleForm({ ...roleForm, permissions: permissions.map(p => p.code) })}
                        className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded hover:bg-primary-200 dark:hover:bg-primary-900/50"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setRoleForm({ ...roleForm, permissions: [] })}
                        className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
                      >
                        Clear All
                      </button>
                    </div>
                  )}
                </div>

                {/* Quick Templates */}
                {roleForm.name !== 'Admin' && !editingRole && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-dark-bg-secondary rounded-lg border border-gray-200 dark:border-dark-border">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Quick Templates:</div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setRoleForm({
                          ...roleForm,
                          permissions: permissions.filter(p =>
                            p.code.includes('.view') ||
                            p.code.includes('profile.')
                          ).map(p => p.code)
                        })}
                        className="text-xs px-3 py-1 bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 rounded-full hover:bg-success-200 dark:hover:bg-success-900/50"
                      >
                        Read-Only
                      </button>
                      <button
                        type="button"
                        onClick={() => setRoleForm({
                          ...roleForm,
                          permissions: permissions.filter(p =>
                            p.code.includes('contracts.') ||
                            p.code.includes('suppliers.') ||
                            p.code.includes('reports.') ||
                            p.code.includes('profile.')
                          ).map(p => p.code)
                        })}
                        className="text-xs px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full hover:bg-primary-200 dark:hover:bg-primary-900/50"
                      >
                        Business User
                      </button>
                      <button
                        type="button"
                        onClick={() => setRoleForm({
                          ...roleForm,
                          permissions: permissions.filter(p =>
                            !p.code.includes('users.') &&
                            !p.code.includes('roles.') &&
                            !p.code.includes('system.')
                          ).map(p => p.code)
                        })}
                        className="text-xs px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full hover:bg-purple-200 dark:hover:bg-purple-900/50"
                      >
                        Manager
                      </button>
                    </div>
                  </div>
                )}

                {/* Permissions by Module */}
                <div className="border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden">
                  <div className="flex justify-between items-center p-3 bg-gray-100 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Permissions by Module</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const allModules = Array.from(new Set(permissions.map(p => p.module)));
                          setExpandedModules(new Set(allModules));
                        }}
                        className="text-xs px-2 py-1 text-primary-600 dark:text-accent-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded"
                      >
                        Expand All
                      </button>
                      <button
                        type="button"
                        onClick={() => setExpandedModules(new Set())}
                        className="text-xs px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded"
                      >
                        Collapse All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-3 max-h-96 overflow-y-auto p-4 bg-white dark:bg-dark-surface">
                  {Array.from(new Set(permissions.map(p => p.module))).map(module => {
                    const modulePermissions = permissions.filter(p => p.module === module);
                    const selectedCount = modulePermissions.filter(p => roleForm.permissions.includes(p.code)).length;
                    const totalCount = modulePermissions.length;

                    const isExpanded = expandedModules.has(module);

                    return (
                      <div key={module} className="border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                        <div
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
                          onClick={() => toggleModuleExpansion(module)}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              )}
                              <span className="font-medium text-gray-900 dark:text-white">{module}</span>
                            </div>
                            <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full">
                              {selectedCount}/{totalCount}
                            </span>
                          </div>
                          {roleForm.name !== 'Admin' && isExpanded && (
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => {
                                  const modulePermissionCodes = modulePermissions.map(p => p.code);
                                  const otherPermissions = roleForm.permissions.filter(p =>
                                    !modulePermissionCodes.includes(p)
                                  );
                                  setRoleForm({
                                    ...roleForm,
                                    permissions: [...otherPermissions, ...modulePermissionCodes]
                                  });
                                }}
                                className="text-xs px-2 py-1 text-primary-600 dark:text-accent-400 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded"
                              >
                                All
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const modulePermissionCodes = modulePermissions.map(p => p.code);
                                  setRoleForm({
                                    ...roleForm,
                                    permissions: roleForm.permissions.filter(p =>
                                      !modulePermissionCodes.includes(p)
                                    )
                                  });
                                }}
                                className="text-xs px-2 py-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-surface-raised rounded"
                              >
                                None
                              </button>
                            </div>
                          )}
                        </div>
                        {isExpanded && (
                          <div className="p-3 space-y-1 bg-white dark:bg-dark-surface">
                            {modulePermissions.map(permission => (
                              <label key={permission.id} className="flex items-start hover:bg-gray-50 dark:hover:bg-dark-bg-secondary p-3 rounded-lg transition-colors cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={roleForm.name === 'Admin' || roleForm.permissions.includes(permission.code)}
                                  disabled={roleForm.name === 'Admin'}
                                  onChange={(e) => {
                                    if (roleForm.name === 'Admin') return;
                                    if (e.target.checked) {
                                      setRoleForm({
                                        ...roleForm,
                                        permissions: [...roleForm.permissions, permission.code]
                                      });
                                    } else {
                                      setRoleForm({
                                        ...roleForm,
                                        permissions: roleForm.permissions.filter(p => p !== permission.code)
                                      });
                                    }
                                  }}
                                  className={`h-4 w-4 text-primary-600 dark:text-accent-400 focus:ring-primary-500 dark:focus:ring-accent-400 border-gray-300 dark:border-dark-border rounded mt-1 flex-shrink-0 bg-white dark:bg-dark-bg-secondary ${
                                    roleForm.name === 'Admin' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                  }`}
                                />
                                <div className="ml-3 min-w-0 flex-1">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white leading-5">{permission.name}</div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-4">{permission.description}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  </div>
                </div>

                {roleForm.name === 'Admin' && (
                  <div className="mt-3 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-500/30 rounded-lg">
                    <div className="text-xs text-error-600 dark:text-error-400">
                      Admin role permissions are system-protected and cannot be modified to ensure system security.
                    </div>
                  </div>
                )}

                {roleForm.name !== 'Admin' && (
                  <div className="mt-3 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-500/30 rounded-lg">
                    <div className="text-xs text-primary-600 dark:text-primary-400">
                      Tip: Use quick templates above to get started, then customize individual permissions as needed.
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-dark-border">
                <button
                  type="button"
                  onClick={() => {
                    setShowRoleModal(false);
                    setEditingRole(null);
                    setRoleForm({ name: '', description: '', permissions: [] });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-secondary rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 dark:from-accent-500 dark:to-accent-400 text-white dark:text-gray-900 rounded-lg hover:shadow-lg hover:shadow-primary-500/25 dark:hover:shadow-accent-500/25 transition-all duration-200 font-medium"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}; 